import { titleCase } from 'common/helpers/humanize';
import { getLocationEndLabel } from './locationOrdering';

/**
 * Shared helpers for digital twin component trees (3D modal, context panel, rails).
 * Keeps display logic aligned across surfaces.
 */

export const CONDITION_LABEL = {
  1: 'Good',
  2: 'Warning',
  3: 'Critical',
};

export const OVERVIEW_PROP_KEYS = new Set([
  'manufacturer',
  'Manufacturer',
  'model',
  'model_number',
  'speed',
  'Speed',
  'power',
  'Power',
]);

export function findComponentNode(components, id) {
  if (!components) return null;
  const stack = Array.isArray(components) ? [...components] : [components];
  while (stack.length) {
    const node = stack.pop();
    if (node) {
      if (String(node.id) === String(id)) return node;
      if (node.components && node.components.length) {
        stack.push(...node.components);
      }
    }
  }
  return null;
}

/**
 * Find a location/bearing row on a machine-info component (same model as 2D / Machine Info).
 * @param {object|null} infoComponent
 * @param {string|number} locationId
 * @returns {object|null}
 */
function findInfoLocationSlotById(infoComponent, locationId) {
  if (!infoComponent) return null;
  const idStr = String(locationId);
  const match = (arr) => {
    if (!Array.isArray(arr)) return null;
    return arr.find(loc => String(loc.id) === idStr) || null;
  };
  return match(infoComponent.locations) || match(infoComponent.bearings) || null;
}

/**
 * Count of motes + tx_sensors for a location id, from machine details (not digital-twin scene).
 */
export function getAssignedSensorCountForLocation(infoComponent, locationId) {
  const loc = findInfoLocationSlotById(infoComponent, locationId);
  if (!loc) return 0;
  const tx = (loc.tx_sensors && loc.tx_sensors.length) || 0;
  const m = (loc.motes && loc.motes.length) || 0;
  return tx + m;
}

/**
 * All sensors for a machine-info component with location labels (Machine Info / 2D twin source).
 */
export function collectAssignedSensorsFlatForComponent(infoComponent, t) {
  if (!infoComponent) return [];
  const out = [];
  const take = (loc) => {
    // Name the end before falling back to the platform's L-number: "L1" tells a reader
    // nothing, and on pump/compressor/crusher and five more families L1 is the FREE end,
    // which reads as the two being swapped.
    const name =
      getLocationEndLabel(loc, t)
      || loc.name
      || loc.description
      || loc.location_type_name
      || (loc.id != null ? `Location ${loc.id}` : 'Location');
    (loc.tx_sensors || []).forEach((s) => {
      out.push({
        ...s,
        location_id: s.location_id == null ? loc.id : s.location_id,
        locationName: name,
      });
    });
    (loc.motes || []).forEach((s) => {
      out.push({
        ...s,
        location_id: s.location_id == null ? loc.id : s.location_id,
        locationName: name,
      });
    });
  };
  (infoComponent.locations || []).forEach(take);
  const br = infoComponent.bearings;
  if (Array.isArray(br)) {
    br.forEach(take);
  } else if (br && typeof br === 'object') {
    Object.values(br).forEach(take);
  }
  return out;
}

export const formatTitleText = titleCase;

/**
 * A component type/subtype in the reader's language.
 *
 * Reuses `diagnosticRules.form.componentSubtypeOptions`, which already names 75 of the
 * platform's 78 subtypes in all five locales, rather than opening a sixth parallel
 * component-type vocabulary — the bundle carries four already and they disagree on both
 * coverage and capitalisation.
 *
 * Anything the table does not name (a new subtype, an umbrella type like "pump") falls back to
 * `fallbackText` when the caller has a better English label than the slug — the palette holds
 * one the backend supplied — and to the title-cased slug otherwise, which is what every surface
 * showed before.
 */
export function translateComponentType(rawType, t, fallbackText) {
  const raw = String(rawType || '');
  if (!raw) return fallbackText || '';
  const fallback = fallbackText || titleCase(raw);
  if (!t) return fallback;
  return t(
    `diagnosticRules.form.componentSubtypeOptions.${raw.toLowerCase()}`,
    { defaultValue: fallback }
  );
}

function serializeStructuredValue(value) {
  // Tracks the current ANCESTOR PATH, not every object ever visited.
  //
  // A cycle means "this object contains itself", so only ancestors can prove
  // one. The previous implementation added every visited object to a WeakSet
  // and never removed it, which also flagged an object that merely appeared
  // twice in sibling positions — a config referencing one shared unit object
  // from two fields rendered the second as the literal string "[Circular]".
  const path = [];
  try {
    // Not an arrow function: JSON.stringify calls the replacer with `this` set
    // to the object currently being serialised, which is what lets us unwind
    // the path when a subtree finishes.
    return JSON.stringify(value, function replacer(key, nested) {
      if (typeof nested !== 'object' || nested === null) return nested;
      while (path.length > 0 && path[path.length - 1] !== this) path.pop();
      if (path.indexOf(nested) !== -1) return '[Circular]';
      path.push(nested);
      return nested;
    });
  } catch (e) {
    return '';
  }
}

/**
 * @param {*} val
 * @param {string} [emptyDisplay='—']
 * @param {number} [jsonMax=120] max length before ellipsis for serialized objects
 */
export function formatPropertyValue(val, emptyDisplay = '—', jsonMax = 120) {
  if (val === null || val === undefined) return emptyDisplay;
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (typeof val === 'number') return Number.isFinite(val) ? String(val) : emptyDisplay;
  if (typeof val === 'string') return val || emptyDisplay;
  if (Array.isArray(val)) return val.length ? val.map(String).join(', ') : emptyDisplay;
  const json = serializeStructuredValue(val);
  if (!json) return emptyDisplay;
  return json.length > jsonMax ? `${json.slice(0, jsonMax - 3)}...` : json;
}

export function formatMeasurementStrategy(asset, emptyDisplay = '—') {
  if (!asset || !asset.measurement_strategy) return emptyDisplay;
  if (Array.isArray(asset.measurement_strategy)) {
    const item = asset.measurement_strategy[0];
    const raw = (item && (item.type || item.strategy)) || '';
    const titled = formatTitleText(raw);
    return titled || emptyDisplay;
  }
  const ms = asset.measurement_strategy;
  const raw = (ms && (ms.type || ms.strategy)) || '';
  const titled = formatTitleText(raw);
  return titled || emptyDisplay;
}
